import React from 'react';
import { UserCheck, ArrowRightLeft, Hand, RotateCcw } from 'lucide-react';

interface OwnershipBannerProps {
  ownerName: string | null;
  department: string | null;
  isOwner: boolean;
  canTakeOver: boolean;
  canTransfer: boolean;
  canAssume: boolean;
  isClosed?: boolean;
  isReopening?: boolean;
  onAssume: () => void;
  onTransfer: () => void;
  onTakeOver: () => void;
  onReopen?: () => void;
}

export default function OwnershipBanner({
  ownerName,
  department,
  isOwner,
  canTakeOver,
  canTransfer,
  canAssume,
  isClosed,
  isReopening,
  onAssume,
  onTransfer,
  onTakeOver,
  onReopen,
}: OwnershipBannerProps) {
  // Chamado encerrado — permite assumir (reabrir)
  if (isClosed) {
    return (
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-100 px-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">
            Chamado encerrado
            {ownerName && (
              <span className="ml-1 text-gray-400">
                — atendido por {isOwner ? 'voce' : ownerName}
              </span>
            )}
          </span>
        </div>
        {onReopen && (
          <button
            type="button"
            onClick={onReopen}
            disabled={isReopening}
            className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RotateCcw size={12} className={isReopening ? 'animate-spin' : ''} />
            {isReopening ? 'Abrindo...' : 'Assumir'}
          </button>
        )}
      </div>
    );
  }
  // Sem atendente - fila de espera (nao atribuido)
  if (!ownerName) {
    return (
      <div className="flex items-center justify-between border-b border-amber-100 bg-amber-50 px-4 py-2">
        <div className="flex items-center gap-2">
          <Hand size={14} className="text-amber-500" />
          <span className="text-xs font-medium text-amber-700">
            Sem atendente atribuido - na fila de espera
            {department && <span className="ml-1 text-amber-500">({department})</span>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canTransfer && (
            <button
              type="button"
              onClick={onTransfer}
              className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              <ArrowRightLeft size={12} />
              Atribuir
            </button>
          )}
          {canAssume && (
            <button
              type="button"
              onClick={onAssume}
              className="rounded-lg bg-green-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-green-700"
            >
              Assumir
            </button>
          )}
        </div>
      </div>
    );
  }

  // Atendido pelo usuario atual
  if (isOwner) {
    return (
      <div className="flex items-center justify-between border-b border-green-100 bg-green-50 px-4 py-2">
        <div className="flex items-center gap-2">
          <UserCheck size={14} className="text-green-600" />
          <span className="text-xs font-medium text-green-700">
            Você está atendendo
            {department && <span className="ml-1 text-green-500">({department})</span>}
          </span>
        </div>
        {canTransfer && (
          <button
            type="button"
            onClick={onTransfer}
            className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            <ArrowRightLeft size={12} />
            Transferir
          </button>
        )}
      </div>
    );
  }

  // Atendido por outra pessoa
  return (
    <div className="flex items-center justify-between border-b border-blue-100 bg-blue-50 px-4 py-2">
      <div className="flex items-center gap-2">
        <UserCheck size={14} className="text-blue-500" />
        <span className="text-xs font-medium text-blue-700">
          Em atendimento por <strong>{ownerName}</strong>
          {department && <span className="ml-1 text-blue-500">— {department}</span>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {canTransfer && (
          <button
            type="button"
            onClick={onTransfer}
            className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            <ArrowRightLeft size={12} />
            Transferir
          </button>
        )}
        {canTakeOver && (
          <button
            type="button"
            onClick={onTakeOver}
            className="rounded-lg bg-blue-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
          >
            Assumir
          </button>
        )}
      </div>
    </div>
  );
}
